import { HasCapability, HasService, useCapability, useService } from 'Components/capabilities'
import { Field, FieldGroup, Form } from 'Components/form'
import { isValidEmail } from 'Components/form/formContext'
import { AvatarEditor, Button, Datepicker, LocationTree, Switch, TextInput } from 'Components/input'
import { FlexColumn, FlexRow } from 'Components/layout'
import { ModalActionsFooter } from 'Components/readonly'
import { AuthContext } from 'Context'
import { parse } from 'date-fns'
import React, { useContext, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { ReplaceSuperUserDropdown } from './replaceSuperUser'
import RoleDropdown from './roleDropdown'
import RoleHelpPanel from './roleHelpPanel'
import RoleHelpPanelShowAll from './roleHelpPanelShowAll'
import UiLanguageDropdown from './uiLanguageDropdown'

const RoleHelpWrapper = styled.div`
  flex: 1 1 auto;

  ${RoleHelpPanel}, ${RoleHelpPanelShowAll} {
    width: auto;
  }
`

export const UserDialogFooter = ({ modalController, onClickSave, isLoading }) => {
  const intl = useIntl()

  return (
    <FlexColumn>
      <ModalActionsFooter>
        <Button label={intl.formatMessage({ id: 'common.cancel' })} onClick={modalController.close}/>
        <Button primary icon="pi pi-save" label={intl.formatMessage({ id: 'common.save' })} isLoading={isLoading}
                onClick={onClickSave}/>
      </ModalActionsFooter>
    </FlexColumn>
  )
}

export const UserDialogContent = ({modalController, formRef, onSubmit, isLoading, setIsLoading}) => {
  const intl = useIntl()

  const {user: selectedUser, organisation} = modalController.data
  const [userMarkets, setUserMarkets] = useState(selectedUser ? selectedUser.userMarkets : [])

  // If editing logged in user show and save profile only
  const loggedIn = useContext(AuthContext).user
  const isProfileMode = selectedUser && selectedUser.id === loggedIn.user.id
  const isNewUser = selectedUser === null

  const defaultRoles = {}
  if (organisation.has_sds && organisation.has_chemical_list) {
    defaultRoles['sds'] = 'not_authorized'
    defaultRoles['chemical_list'] = 'not_authorized'
  } else if (organisation.has_sds) {
    defaultRoles['sds'] = 'user'
    defaultRoles['chemical_list'] = 'not_authorized'
  } else if (organisation.has_chemical_list) {
    defaultRoles['sds'] = 'not_authorized'
    defaultRoles['chemical_list'] = 'user'
  } else {
    defaultRoles['sds'] = 'not_authorized'
    defaultRoles['chemical_list'] = 'not_authorized'
  }

  const initialFormData = {
    name: (selectedUser && selectedUser.name) || '',
    phone: (selectedUser && selectedUser.phone) || '',
    email: (selectedUser && selectedUser.email) || '',
    starts_at: !selectedUser ? new Date() : selectedUser.starts_at && parse(selectedUser.starts_at),
    expires_at: (selectedUser && selectedUser.expires_at && parse(selectedUser.expires_at)) || '',
    roles: (selectedUser && selectedUser.roleValues) || defaultRoles,
    language: (selectedUser && selectedUser.language) || 'sv',
    limit_locations: (selectedUser && selectedUser.limit_locations) || [],
    avatar: (selectedUser && selectedUser.avatar) || null,
    module: (selectedUser && selectedUser.module) || {},
    module_access: (selectedUser && selectedUser.module_access) || {},
    sds_start_date: (selectedUser && selectedUser?.module_access?.SDS?.start_date && parse(selectedUser.module_access.SDS.start_date)) || '',
    sds_end_date: (selectedUser && selectedUser?.module_access?.SDS?.end_date && parse(selectedUser.module_access.SDS.end_date)) || '',
    cm_start_date: (selectedUser && selectedUser?.module_access?.CM?.start_date && parse(selectedUser.module_access.CM.start_date)) || '',
    cm_end_date: (selectedUser && selectedUser?.module_access?.CM?.end_date && parse(selectedUser.module_access.CM.end_date)) || '',
  }

  const [currentRoles, setCurrentRoles] = useState(initialFormData.module)


  // Handle single market differently in form
  const markets = organisation.organisationMarkets ?? [];
  const isSingleMarket = markets.length === 1;
  const singleMarket = isSingleMarket ? markets[0] : null;

  // If single market, set user market programatically
  useEffect(() => {
    if (!isSingleMarket || !singleMarket) {
      return;
    }

    // Non authorized and single market
    if (currentRoles.chemical_list === 'not_authorized') {
      if (userMarkets?.includes(singleMarket)) {
        setUserMarketValue(singleMarket, false);
      }
      return;
    }

    // Authorized and single market
    if (!userMarkets?.includes(singleMarket)) {
      setUserMarketValue(singleMarket, true);
    }
  }, [
    currentRoles.chemical_list,
    isSingleMarket,
    singleMarket,
    userMarkets,
  ]);

  const validateSdsRoles = () => {
    if (!organisation.has_sds) {
      return null
    }

    if (!Object.values(currentRoles).some((role) => role !== 'not_authorized')) {
      return {id: 'admin.validation_all_roles_not_authorized'}
    }
  }

  const validateChemicalRoles = () => {
    if (!organisation.has_chemical_list) {
      return null
    }

    if (!Object.values(currentRoles).some((role) => role !== 'not_authorized')) {
      return {id: 'admin.validation_all_roles_not_authorized'}
    }

    if (currentRoles.chemical_list !== 'not_authorized') {
      if (!Object.values(userMarkets).some((market) => market !== false)) {
        return {id: 'admin.validation_no_market_added'}
      }
    }
  }

  const hasServiceChemicalList = useService('chemical_list')
  const hasServiceSds = useService('sds')
  const hasCapabilityManageRolesChemicalList = useCapability('MANAGE_ROLES_CHEMICAL_LIST')
  const hasCapabilityManageRolesSds = useCapability('MANAGE_ROLES_SDS')
  const mayEditRolesChemicalList = hasServiceChemicalList && hasCapabilityManageRolesChemicalList
  const mayEditRolesSds = hasServiceSds && hasCapabilityManageRolesSds

  const emailValidation = (value) => {
    if (!isValidEmail(value)) {
      return {id: 'validation.email', values: {attribute: intl.formatMessage({id: 'common.email'})}}
    }
  }

  const setUserMarketValue = (market, enabled) => {
    setUserMarkets((prev = []) => {
      if (enabled) {
        return prev.includes(market) ? prev : [...prev, market];
      }

      return prev.filter((item) => item !== market);
    });
  };

  const handleUserMarketToggle = (market, e) => {
    setUserMarketValue(market, e.target.value);
  }

  return (
    <div>
      <Form ref={formRef}
            onSubmit={(formData) => onSubmit(formData, userMarkets, selectedUser, isNewUser, isProfileMode)}>
        <FlexRow>
          <FieldGroup columns="1">
            <Field
              id="name"
              label={intl.formatMessage({id: 'common.name'})}
              focus="true"
              Component={TextInput}
              initialValue={initialFormData.name}
              required={true}
            />
            <Field
              id="phone"
              label={intl.formatMessage({id: 'common.phone'})}
              Component={TextInput}
              initialValue={initialFormData.phone}
            />
            {isNewUser ? (
              <Field
                id="email"
                label={intl.formatMessage({id: 'common.email'})}
                Component={TextInput}
                initialValue={initialFormData.email}
                required={true}
                validation={emailValidation}
              />
            ) : null}
            {!isProfileMode && (
              <>

                <HasService service="sds">
                  <HasCapability capability="MANAGE_ROLES_SDS">
                    <Field
                      id="roles.sds"
                      label={intl.formatMessage({id: 'user.role'}) + ' ' + intl.formatMessage({id: 'sds.module'})}
                      Component={RoleDropdown}
                      initialValue={initialFormData.module.sds}
                      required={mayEditRolesSds}
                      onChange={(e) => setCurrentRoles({...currentRoles, sds: e.target.value})}
                      validation={validateSdsRoles}
                    />

                    {currentRoles.sds !== 'not_authorized' &&
                      <>
                        <Field
                          id="module_access[SDS][start_date]"
                          label={intl.formatMessage({id: 'common.start_date'})}
                          dateFormat="yy-mm-dd"
                          Component={Datepicker}
                          initialValue={initialFormData.sds_start_date}
                          validation="date"
                          appendTo={document.body}
                        />
                        <Field
                          id="module_access[SDS][end_date]"
                          label={intl.formatMessage({id: 'common.end_date'})}
                          dateFormat="yy-mm-dd"
                          Component={Datepicker}
                          initialValue={initialFormData.sds_end_date}
                          validation="date"
                          appendTo={document.body}
                        />
                      </>
                    }
                  </HasCapability>
                </HasService>
                <HasService service="chemical_list">
                  <HasCapability capability="MANAGE_ROLES_CHEMICAL_LIST">
                    <Field
                      id="roles.chemical_list"
                      label={intl.formatMessage({id: 'user.role'}) + ' ' + intl.formatMessage({id: 'chemicals.module'})}
                      Component={RoleDropdown}
                      initialValue={initialFormData.module.chemical_list}
                      required={mayEditRolesChemicalList}
                      onChange={(e) => {
                        setCurrentRoles({...currentRoles, chemical_list: e.target.value})
                      }}
                      validation={validateChemicalRoles}
                    />

                    {currentRoles.chemical_list !== 'not_authorized' &&
                      <>
                        <Field
                          id="module_access[CM][start_date]"
                          label={intl.formatMessage({id: 'common.start_date'})}
                          dateFormat="yy-mm-dd"
                          Component={Datepicker}
                          initialValue={initialFormData.cm_start_date}
                          validation="date"
                          appendTo={document.body}
                        />
                        <Field
                          id="module_access[CM][end_date]"
                          label={intl.formatMessage({id: 'common.end_date'})}
                          dateFormat="yy-mm-dd"
                          Component={Datepicker}
                          initialValue={initialFormData.cm_end_date}
                          validation="date"
                          appendTo={document.body}
                        />
                      </>
                    }

                    {!isSingleMarket &&
                      markets &&
                      currentRoles.chemical_list !== 'not_authorized' &&
                      markets.map((market) => (
                        <Field
                          id={'enable_user_market_' + market}
                          key={market}
                          Component={Switch}
                          label={
                            intl.formatMessage({id: 'common.activate_market_' + market}) +
                            ' (' +
                            intl.formatMessage({id: 'chemicals.module'}) +
                            ')'
                          }
                          onLabel={intl.formatMessage({id: 'common.yes'})}
                          offLabel={intl.formatMessage({id: 'common.no'})}
                          onChange={(e) => handleUserMarketToggle(market, e)}
                          disabled={currentRoles.chemical_list === 'not_authorized'}
                          value={userMarkets ? userMarkets.includes(market) : false}
                          required={currentRoles.chemical_list !== 'not_authorized' && mayEditRolesChemicalList}
                        />
                      ))}
                    {currentRoles.chemical_list !== 'admin' && (
                      <>
                        {currentRoles.chemical_list !== 'not_authorized' && (
                          <Field
                            id="limit_locations"
                            label={intl.formatMessage({id: 'user.limit_locations'})}
                            Component={LocationTree}
                            appendToBody={true}
                            multiselect={true}
                            childrenincluded={true}
                            initialValue={initialFormData.limit_locations}
                            width="200px"
                          />
                        )}

                        {selectedUser !== null && (
                          <Field
                            id="replace_super_user"
                            Component={ReplaceSuperUserDropdown}
                            user={selectedUser}
                            required={mayEditRolesChemicalList && currentRoles.chemical_list !== 'admin'}
                          />
                        )}
                      </>
                    )}
                  </HasCapability>
                </HasService>
              </>
            )}

            <Field
              id="language"
              label={intl.formatMessage({id: 'common.language'})}
              Component={UiLanguageDropdown}
              initialValue={initialFormData.language}
              required={true}
              endpoint="user"
            />

            <Field
              id="avatar"
              Component={AvatarEditor}
              label={intl.formatMessage({id: 'user.avatar'})}
              initialValue={initialFormData.avatar}
              validation={(value) => {
                return value === '__editing_ongoing__' ? {id: 'user.avatar-validation-confirm-first'} : null
              }}
            />
          </FieldGroup>
          <RoleHelpWrapper className="md-show">
            {organisation.has_chemical_list || organisation.has_sds ? <RoleHelpPanel/> : <RoleHelpPanelShowAll/>}
          </RoleHelpWrapper>
        </FlexRow>
      </Form>
    </div>
  )
};
